// src/services/parentNarrativeGenerator.ts
import type { ChildProgress } from './analytics'

interface NarrativeParagraph {
  type: 'summary' | 'strength' | 'challenge' | 'pattern' | 'recommendation'
  text: string
  tone: 'positive' | 'neutral' | 'concerned'
}

export class ParentNarrativeGenerator {
  
  generate(progress: ChildProgress, childName: string): NarrativeParagraph[] {
      const narrative: NarrativeParagraph[] = []
       
      // 1. Opening summary
      narrative.push(this.generateOpeningSummary(progress, childName))
        
      // 2. Engagement patterns
      narrative.push(this.generateEngagementPattern(progress, childName))
        
      // 3. Skill development
      if (progress.strengths.length > 0 || progress.practicingAreas.length > 0) {
          narrative.push(this.generateSkillDevelopment(progress, childName))
      }
        
      // 4. Time/routine patterns
      const timePattern = this.detectTimePattern(progress)
      if (timePattern) {
          narrative.push(timePattern)
      }
        
      // 5. Check for concerning patterns
      const concerningPattern = this.detectConcerningPatterns(progress, childName)
      if (concerningPattern) {
          narrative.push(concerningPattern)
      }
        
      // 6. This week's focus
      narrative.push(this.generateWeeklyFocus(progress, childName))
      
      return narrative
    }
  
  private generateOpeningSummary(progress: ChildProgress, name: string): NarrativeParagraph {
    const avgSession = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    const isConsistent = progress.currentStreak >= 3
    
    let text = ''
    let tone: 'positive' | 'neutral' = 'positive'
    
    if (avgSession > 20 && isConsistent) {
      text = `${name} is doing really well over the last few weeks. They're building a strong learning routine and staying engaged for solid stretches of time.`
    } else if (avgSession > 15) {
      text = `${name} is making steady progress. They're engaging consistently with the activities and building good focus habits.`
    } else if (avgSession > 10) {
      text = `${name} is working through activities at a comfortable pace. They're taking their time with the material, which is completely appropriate.`
      tone = 'neutral'
    } else {
      text = `${name} is engaging in shorter learning sessions right now. This might reflect their current energy levels or schedule - short, focused practice can still be very effective.`
      tone = 'neutral'
    }
    
    return { type: 'summary', text, tone }
  }
  
  private generateEngagementPattern(progress: ChildProgress, name: string): NarrativeParagraph {
    const avgSession = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    
    let text = ''
    
    if (avgSession > 20) {
      text = `${name} seems to be getting more comfortable with the platform. Their focus during sessions has been strong, which suggests they're finding the right challenge level.`
    } else if (avgSession < 8) {
      text = `${name} tends to work in quick bursts. This learning style can work well - some kids process better in short, focused chunks rather than long sessions.`
    } else {
      text = `${name}'s session lengths are pretty typical for learners their age. They're pacing themselves well without getting overwhelmed.`
    }
    
    return { type: 'pattern', text, tone: 'neutral' }
  }
  
  private generateSkillDevelopment(progress: ChildProgress, name: string): NarrativeParagraph {
    let text = ''
    let tone: 'positive' | 'neutral' = 'positive'
    
    const topStrength = progress.strengths?.[0] || null
    const topChallenge = progress.practicingAreas?.[0] || null
    
    if (topStrength && !topChallenge) {
      text = `${name} is showing real strength in ${this.humanizeSkill(topStrength)}. This is building their confidence nicely.`
    } else if (topStrength && topChallenge) {
      text = `${name} seems to be getting more comfortable with ${this.humanizeSkill(topStrength)}. They're working on ${this.humanizeSkill(topChallenge)} - this is exactly where they should be challenged right now.`
      tone = 'neutral'
    } else if (topChallenge) {
      text = `${name} is working through ${this.humanizeSkill(topChallenge)}. It's good they're getting practice in this area - it means the platform is finding their growing edge.`
      tone = 'neutral'
    } else {
      text = `${name} is exploring different skill areas. We're still gathering data on where they're strongest.`
      tone = 'neutral'
    }
    
    return { type: 'strength', text, tone }
  }
  
  private detectTimePattern(progress: ChildProgress): NarrativeParagraph | null {
    // Look at week vs total to detect recent changes
    const recentEngagement = progress.weekMinutes / 7
    const historicalAvg = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    
    if (recentEngagement > historicalAvg * 1.3) {
      return {
        type: 'pattern',
        text: `I'm noticing increased engagement this week compared to their usual pattern. Something seems to be clicking for them.`,
        tone: 'positive'
      }
    } else if (recentEngagement < historicalAvg * 0.6 && progress.totalSessions > 5) {
      return {
        type: 'pattern',
        text: `Their engagement this week is lighter than usual. This could be totally normal - kids go through natural rhythms with learning.`,
        tone: 'neutral'
      }
    }
    
    return null
  }
  
  private detectConcerningPatterns(progress: ChildProgress, name: string): NarrativeParagraph | null {
    const avgSession = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    
    // Sudden engagement drop after previous consistency
    if (progress.totalSessions > 8 && progress.currentStreak === 0 && progress.weekMinutes < 15) {
      return {
        type: 'challenge',
        text: `I'm noticing ${name} hasn't engaged much this past week, which is unusual for them. This might mean they're hitting a frustration point with the current material, or it could be unrelated to the platform (sick week, busy schedule, etc.). Worth checking in: "Hey, how's the learning stuff going lately?"`,
        tone: 'concerned'
      }
    }
    
    // Never builds momentum (might be too hard)
    if (progress.totalSessions > 6 && avgSession < 5) {
      return {
        type: 'challenge',
        text: `${name} tends to disengage quickly across multiple sessions. This pattern sometimes means the material feels too hard or the format isn't clicking yet. Try switching to a different activity type - sometimes kids need hands-on practice instead of reading, or vice versa.`,
        tone: 'concerned'
      }
    }
    
    return null
  }
  
  private generateWeeklyFocus(progress: ChildProgress, name: string): NarrativeParagraph {
    const avgSession = progress.totalMinutes / Math.max(progress.totalSessions, 1)
    
    let text = `**Try this week:** `
    
    if (avgSession < 10) {
      text += `Set a timer for just 8 minutes. When it goes off, let ${name} choose: keep going or stop. Often they'll choose to continue once the pressure's off.`
    } else if (avgSession > 20) {
      text += `${name}'s got stamina! Try adding a 2-minute movement break halfway through - it can actually extend their focus even longer.`
    } else {
      text += `${name}'s finding a good rhythm. Keep sessions around 15 minutes and celebrate effort, not completion.`
    }
    
    return { type: 'recommendation', text, tone: 'positive' }
  }
  
  private humanizeSkill(skill: string): string {
    const skillMap: Record<string, string> = {
      'word_building': 'putting letters together to make words',
      'phonics': 'connecting sounds to letters (like knowing "c-a-t" says cat)',
      'reading_fluency': 'reading smoothly without stopping at every word',
      'comprehension': 'understanding what they read',
      'sentence_building': 'writing complete sentences',
      'spelling': 'spelling words correctly',
      'vocabulary': 'learning new words and their meanings'
    }
    
    return skillMap[skill] || skill.replace(/_/g, ' ')
  }
}

export const narrativeGenerator = new ParentNarrativeGenerator()